import { type ReactNode, useState } from "react";
import { theme } from "src/lib/emotion/theme.ts";
import { DataGrid } from "src/components/DataGrid/DataGrid.tsx";
import {
  TransferListButton,
  TransferListTitle,
} from "./TransferListElements.tsx";
import { ArrowToLeftIcon, ArrowToRightIcon } from "src/lib/ui-icon-v2";
import { ArrowsToLeftIcon, ArrowsToRightIcon } from "src/lib/ui-icon-v2";

import * as styles from "./transferList.styles.ts";

import type {
  GridValidRowModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import type { DataGridProps } from "src/components/DataGrid/DataGrid.tsx";
import { useControlled } from "@exonn/hooks";
import { TooltipV2 } from "src/components/TooltipV2/TooltipV2.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";

import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";

await loadTranslations(TranslationNamespaceEnum.TransferListComponent, [
  "en",
  "de",
]);

type GridRow = GridValidRowModel & { id: string };

export type TransferListGridRow = GridRow;

const commonDataGridProps: Omit<DataGridProps, "columns"> = {
  disableColumnMenu: true,
  disableColumnSelector: true,
  disableColumnSorting: true,
  disableColumnReorder: true,
  sx: {
    borderRadius: theme.borderRadiusV2.primary,
  },
  slots: {
    noRowsOverlay: () => null,
  },
};

const TOOLTIP_DELAY = {
  open: 1000,
  close: 0,
};

export type TransferListProps<Row extends GridRow = GridRow> = {
  className?: string;
  hideTransferSelectedToRightButton?: boolean;
  hideTransferSelectedToLeftButton?: boolean;
  hideTransferAllToLeftButton?: boolean;
  hideTransferAllToRightButton?: boolean;

  disableTransferSelectedToRightButton?: boolean;
  disableTransferSelectedToLeftButton?: boolean;
  disableTransferAllToLeftButton?: boolean;
  disableTransferAllToRightButton?: boolean;

  onTransferSelectedToRight?: (rows: Row[]) => void;
  onTransferSelectedToLeft?: (rows: Row[]) => void;
  onTransferAllToRight?: () => void;
  onTransferAllToLeft?: () => void;
  dataGridPropsLeft: DataGridProps<Row> & {
    renderSubTitle?: () => ReactNode;
    title: string;
    defaultRows?: Row[];
    onChange?(rows: NoInfer<Row>[]): void;
  };
  dataGridPropsRight: DataGridProps<NoInfer<Row>> & {
    renderSubTitle?: () => ReactNode;
    title: string;
    defaultRows?: NoInfer<Row>[];
    onChange?(rows: NoInfer<Row>[]): void;
  };
};

export function TransferList<Row extends GridRow = GridRow>(
  props: TransferListProps<Row>,
) {
  const {
    className,
    dataGridPropsLeft,
    dataGridPropsRight,
    onTransferAllToLeft,
    onTransferSelectedToLeft,
    onTransferSelectedToRight,
    onTransferAllToRight,
    hideTransferAllToRightButton = false,
    hideTransferAllToLeftButton = false,
    hideTransferSelectedToRightButton = false,
    hideTransferSelectedToLeftButton = false,

    disableTransferAllToRightButton = false,
    disableTransferAllToLeftButton = false,
    disableTransferSelectedToRightButton = false,
    disableTransferSelectedToLeftButton = false,
  } = props;

  const { t } = useTranslation(TranslationNamespaceEnum.TransferListComponent);
  const {
    rows: rowsLeftProp,
    title: leftTitle,
    defaultRows: defaultRowsLeft = [],
    onChange: onChangeLeftRows,
    ...restDataGridPropsL
  } = dataGridPropsLeft;
  const {
    rows: rowsRightProp,
    title: rightTitle,
    defaultRows: defaultRowsRight = [],
    onChange: onChangeRightRows,
    ...restDataGridPropsR
  } = dataGridPropsRight;

  const [rowsL, setRowsL] = useControlled({
    controlled: rowsLeftProp as Row[],
    default: defaultRowsLeft,
    name: "TransferList",
    state: "rowsL",
  });

  const [rowsR, setRowsR] = useControlled({
    controlled: rowsRightProp as Row[],
    default: defaultRowsRight,
    name: "TransferList",
    state: "rowsR",
  });

  const [selectedL, setSelectedL] = useState<GridRowSelectionModel>([]);
  const [selectedR, setSelectedR] = useState<GridRowSelectionModel>([]);

  const handleTransferRight = () => {
    const selectedRows = rowsL.filter((row) => selectedL.includes(row.id));
    const newRowsL = rowsL.filter((row) => !selectedL.includes(row.id));
    const newRowsR = [...rowsR, ...selectedRows];
    setRowsL(newRowsL);
    setRowsR(newRowsR);
    setSelectedL([]);

    onTransferSelectedToRight?.(selectedRows);
    onChangeLeftRows?.(newRowsL);
    onChangeRightRows?.(newRowsR);
  };

  const handleTransferLeft = () => {
    const selectedRows = rowsR.filter((row) => selectedR.includes(row.id));
    const newRowsL = [...rowsL, ...selectedRows];
    const newRowsR = rowsR.filter((row) => !selectedR.includes(row.id));
    setRowsL(newRowsL);
    setRowsR(newRowsR);
    setSelectedR([]);

    onTransferSelectedToLeft?.(selectedRows);
    onChangeLeftRows?.(newRowsL);
    onChangeRightRows?.(newRowsR);
  };

  const handleTransferAllRight = () => {
    const newRowsR = [...rowsR, ...rowsL];
    setRowsL([]);
    setRowsR(newRowsR);
    setSelectedL([]);

    onTransferAllToRight?.();
    onChangeLeftRows?.([]);
    onChangeRightRows?.(newRowsR);
  };

  const handleTransferAllLeft = () => {
    const newRowsL = [...rowsL, ...rowsR];
    setRowsL(newRowsL);
    setRowsR([]);
    setSelectedR([]);
    onTransferAllToLeft?.();
    onChangeLeftRows?.(newRowsL);
    onChangeRightRows?.([]);
  };

  return (
    <div css={styles.root} className={className}>
      <div css={styles.dataGridColumn}>
        <TransferListTitle
          title={leftTitle}
          rowsQty={
            dataGridPropsLeft.paginationMode === "server"
              ? dataGridPropsLeft.rowCount
              : rowsL.length
          }
        />
        {dataGridPropsLeft?.renderSubTitle?.()}

        <div css={styles.dataGridContainer}>
          <DataGrid
            {...commonDataGridProps}
            {...restDataGridPropsL}
            rows={rowsL}
            hideToolbar
            rowSelectionModel={selectedL}
            onRowSelectionModelChange={(selection) => setSelectedL(selection)}
          />
        </div>
      </div>

      <div css={styles.buttonsPanel}>
        {!hideTransferAllToLeftButton && (
          <TooltipV2 delay={TOOLTIP_DELAY} content={t("transfer-all-to-left")}>
            <TransferListButton
              icon={<ArrowsToLeftIcon />}
              onClick={handleTransferAllLeft}
              disabled={
                !rowsR.length ||
                disableTransferAllToLeftButton ||
                !!restDataGridPropsL.loading ||
                !!restDataGridPropsR.loading
              }
            />
          </TooltipV2>
        )}
        {!hideTransferSelectedToLeftButton && (
          <TooltipV2
            delay={TOOLTIP_DELAY}
            content={t("transfer-selected-to-left")}
          >
            <TransferListButton
              icon={<ArrowToLeftIcon />}
              onClick={handleTransferLeft}
              disabled={
                !selectedR.length ||
                disableTransferSelectedToLeftButton ||
                !!restDataGridPropsL.loading ||
                !!restDataGridPropsR.loading
              }
            />
          </TooltipV2>
        )}
        {!hideTransferSelectedToRightButton && (
          <TooltipV2
            delay={TOOLTIP_DELAY}
            content={t("transfer-selected-to-right")}
          >
            <TransferListButton
              icon={<ArrowToRightIcon />}
              onClick={handleTransferRight}
              disabled={
                !selectedL.length ||
                disableTransferSelectedToRightButton ||
                !!restDataGridPropsL.loading ||
                !!restDataGridPropsR.loading
              }
            />
          </TooltipV2>
        )}
        {!hideTransferAllToRightButton && (
          <TooltipV2 delay={TOOLTIP_DELAY} content={t("transfer-all-to-right")}>
            <TransferListButton
              icon={<ArrowsToRightIcon />}
              onClick={handleTransferAllRight}
              disabled={
                !rowsL.length ||
                disableTransferAllToRightButton ||
                !!restDataGridPropsL.loading ||
                !!restDataGridPropsR.loading
              }
            />
          </TooltipV2>
        )}
      </div>

      <div css={styles.dataGridColumn}>
        <TransferListTitle
          title={rightTitle}
          rowsQty={
            dataGridPropsRight.paginationMode === "server"
              ? dataGridPropsRight.rowCount
              : rowsR.length
          }
        />
        {dataGridPropsRight?.renderSubTitle?.()}
        <div css={styles.dataGridContainer}>
          <DataGrid
            {...commonDataGridProps}
            {...restDataGridPropsR}
            rows={rowsR}
            hideToolbar
            rowSelectionModel={selectedR}
            onRowSelectionModelChange={(selection) => setSelectedR(selection)}
          />
        </div>
      </div>
    </div>
  );
}
